/**
 * Last line of defence for the console.
 *
 * React unmounts the whole tree when a render throws, so before this existed a
 * single bad component — a menu label outside its group, say — replaced the
 * entire console with a blank white page and no clue what happened. An
 * operator cannot act on a blank page, and a blank page reports as "the admin
 * is down" rather than "one panel is broken".
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@nt/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * A chunk that will never load.
 *
 * Every page here is `React.lazy`, and React CACHES a rejected import — so
 * clearing local state and re-rendering re-throws the same rejection. "Try
 * again" was a no-op for exactly the case this boundary exists to catch: a
 * hashed chunk 404 after a mid-session deploy. Only a reload can fetch the new
 * manifest.
 */
export function isChunkLoadError(error: Error): boolean {
  if (error.name === "ChunkLoadError") return true;
  return /dynamically imported module|Importing a module script failed|Loading chunk \d+ failed/i.test(
    error.message,
  );
}

export class ConsoleErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept: without the component stack a production crash is far harder to
    // place than the message alone suggests.
    console.error("Console render error", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);

    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-start gap-3 py-6">
            <div className="space-y-1">
              <p className="font-medium">This screen hit an error</p>
              <p className="text-sm text-muted-foreground">
                {chunk
                  ? "The console was updated while this tab was open. Reloading picks up the new version."
                  : "The rest of the console still works — the sidebar will take you somewhere else."}
              </p>
            </div>
            <p className="font-mono text-compact break-all text-muted-foreground">
              {error.message}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                chunk
                  ? window.location.reload()
                  : this.setState({ error: null })
              }
            >
              {chunk ? "Reload" : "Try again"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}
